import React from 'react';
import { observer } from 'mobx-react';
import { Virtuoso } from 'react-virtuoso';
import { Heading, Avatar, Box, Tooltip, Divisor } from 'components';

import { routerStore } from 'core/routing';

// import mainTheme from 'styles/themes/wikifactory';
import { frameStore } from 'apps/components/Frame/appManager';
import { ProgressBar } from 'legacyComponents/progressBar';
import { env } from 'process';
import {
  InboxStyled,
  NoConversationsWrapper,
  ProgressBarWrapper,
  InboxTitleWrapper,
} from './inbox.styled';
import NoConversations from './icons/NoConversations';

import InboxItem from './InboxItem';

import BetaLabel from './BetaLabel';

type InboxProps = {
  onClose: () => void;
};

type Conversation = {
  unreadCount: number;
};

const Inbox = observer((props: InboxProps) => {
  const { onClose } = props;
  const { inboxManager } = frameStore;

  const sortedConversations = inboxManager.conversationsConnManager.nodes
    .slice()
    .sort((a: Conversation, b: Conversation) => {
      if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
      if (b.unreadCount > 0 && a.unreadCount === 0) return 1;
      return 0;
    });

  const { hasDataBeenLoaded, total } = inboxManager.conversationsConnManager;
  const areThereNoConversations = hasDataBeenLoaded && total === 0;
  const isLoadingInitialData = !hasDataBeenLoaded && total === 0;

  return (
    <InboxStyled>
      <InboxTitleWrapper>
        <Box px={3} py={2}>
          <Heading level={4} size='small'>
            Inbox
          </Heading>
        </Box>
        <BetaLabel />
      </InboxTitleWrapper>
      {/* <Box p={2} bg={mainTheme.color.wfGrey50}>
        <Input placeholder='Search' size='xsmall' icon='search' rounded />
      </Box> */}

      {/* TODO use old progress bar for now */}
      <Divisor />
      <ProgressBarWrapper>
        <ProgressBar position='absolute' visible={isLoadingInitialData} />
      </ProgressBarWrapper>
      {areThereNoConversations ? (
        <NoConversationsWrapper>
          <NoConversations />
          <p>No conversations yet.</p>
        </NoConversationsWrapper>
      ) : (
        <Virtuoso
          data={sortedConversations}
          endReached={() => {
            inboxManager.conversationsConnManager.fetchMore();
          }}
          itemContent={(index, conversation) => {
            const { context, type } = conversation;
            const { parentObj } = context;

            if (!conversation.lastMessage || !parentObj) {
              if (env.MODE === 'development')
                // eslint-disable-next-line no-console
                console.log(
                  'Inbox: Got a conversation with null lastMessage',
                  conversation,
                );
              return <div style={{ height: 2 }}></div>;
            }

            const { profile } = conversation.lastMessage.createdBy;
            let fullName = '';

            if (profile !== undefined) {
              fullName = profile.fullName;
            } else {
              fullName = 'Deleted user';
            }

            let itemName = conversation.resourceId;
            let tooltipText = '';
            let displayType = `${type}:`;

            let route;
            if (!conversation.uri || conversation.uri === '') {
              route = { name: 'invalid_uri_empty', params: {} };
            } else {
              route = routerStore.router.matchUrl(conversation.uri);
              if (route === undefined) {
                route = { name: 'invalid_uri_route_match', params: {} };
              }
            }
            const routeName = route.name;

            if (type === 'RFQ') {
              itemName = parentObj.title;
            } else if (type === 'FILE') {
              if (routeName === 'profile.shared.file') {
                // shared files
                displayType = 'File:';
                // TODO: Add the revision here. Not being returned
                itemName = `${parentObj.name}`;
              } else if (routeName === 'invalid_uri') {
                displayType = 'File with invalid URI';
                itemName = `${parentObj.name} ${conversation.uri}`;
              } else {
                // project files
                const projectName = parentObj.name;

                displayType = `${projectName} / File:`;
                tooltipText = conversation.resourceId;

                if (tooltipText) tooltipText = `${projectName}/${tooltipText}`;
                const lastSlashIndex = itemName.lastIndexOf('/');
                const fileName = itemName.substring(lastSlashIndex + 1);
                itemName = fileName;
              }
            } else if (type === 'ORDER') {
              displayType = 'Order:';
              itemName = parentObj.title;
            }

            let routeParams = route.params;
            if (!routeParams) routeParams = {}; // probably unnecessary
            routeParams.panel = 'discuss';

            const conversationTooltipId = `InboxConversationTooltip-${conversation.id}`;
            return (
              <>
                <InboxItem
                  key={conversation.id}
                  avatar={
                    <Avatar
                      name={fullName}
                      src={profile ? profile.avatar?.url : null}
                    />
                  }
                  type={displayType}
                  fileName={itemName}
                  userName={fullName}
                  body={conversation.lastMessage.snippet}
                  date={new Date(conversation.lastMessage.timestamp)}
                  unreadCount={conversation.unreadCount}
                  routeName={routeName}
                  routeParams={routeParams}
                  onClick={() => {
                    onClose();
                  }}
                  titleTooltip={tooltipText}
                  dataFor={conversationTooltipId}
                />
                <Tooltip delayShow={200} id={conversationTooltipId} />
              </>
            );
          }}
        ></Virtuoso>
      )}
    </InboxStyled>
  );
});

export default Inbox;
